import { useState, useEffect } from 'react';
import { RideMap, RideMapPreview } from '../components/RideMap';
import { Search, Navigation, MapPin } from 'lucide-react';
import { BACKEND_URL } from '../utils/apiConfig';

const TestMapPage = () => {
  const [startCity, setStartCity] = useState('Berlin');
  const [destinationCity, setDestinationCity] = useState('Hamburg');
  const [intermediateStops, setIntermediateStops] = useState([]);
  const [apiStatus, setApiStatus] = useState(null);
  const [backendUrl, setBackendUrl] = useState(BACKEND_URL);
  
  // Test-Städte für schnelles Testen
  const testRoutes = [
    { start: 'Berlin', destination: 'Hamburg', label: 'Berlin → Hamburg' },
    { start: 'München', destination: 'Köln', label: 'München → Köln' },
    { start: 'Frankfurt', destination: 'Stuttgart', label: 'Frankfurt → Stuttgart' },
    { start: 'Hannover', destination: 'Leipzig', label: 'Hannover → Leipzig' },
  ];

  // API-Status prüfen
  useEffect(() => {
    checkApiStatus();
  }, []);

  const checkApiStatus = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/tracking/eta?from_city=Berlin&to_city=Hamburg`);
      if (response.ok) {
        const data = await response.json();
        setApiStatus({ ok: true, data });
      } else {
        setApiStatus({ ok: false, error: `API Error ${response.status}` });
      }
    } catch (error) {
      setApiStatus({ ok: false, error: error.message });
    }
  };

  const handleTestRoute = (start, destination) => {
    setStartCity(start);
    setDestinationCity(destination);
  };

  const handleAddStop = () => {
    const stop = prompt('Zwischenstopp eingeben (z.B. "Hannover"):');
    if (stop) {
      setIntermediateStops([...intermediateStops, stop]);
    }
  };

  return (
    <div className="app-container">
      <div className="content-wrapper" style={{ maxWidth: '1200px' }}>
        <h1 className="title">Karten-Test</h1>
        <p className="subtitle">Teste die Leaflet Karte und GPS-Tracking API</p>

        {/* API Status */}
        <div className="api-status" style={{ 
          background: apiStatus?.ok ? '#d1fae5' : '#fee2e2',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <h3>API Status:</h3>
          <p>
            Backend URL: <code>{backendUrl}</code>
          </p>
          {apiStatus ? (
            apiStatus.ok ? (
              <p style={{ color: '#065f46' }}>✅ API erreichbar - Berlin → Hamburg: {apiStatus.data?.distance_km}km, {apiStatus.data?.eta_text}</p>
            ) : (
              <p style={{ color: '#991b1b' }}>❌ API nicht erreichbar: {apiStatus.error}</p>
            )
          ) : (
            <p>⏳ Prüfe API...</p>
          )}
        </div>

        {/* Route Controls */}
        <div className="test-controls" style={{ 
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: '1.5rem'
        }}>
          <h3>Route konfigurieren</h3>
          
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label>Startstadt</label>
              <input
                type="text"
                value={startCity}
                onChange={(e) => setStartCity(e.target.value)}
                placeholder="Startstadt"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            
            <div style={{ flex: 1 }}>
              <label>Zielstadt</label>
              <input
                type="text"
                value={destinationCity}
                onChange={(e) => setDestinationCity(e.target.value)}
                placeholder="Zielstadt"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>

          {/* Zwischenstopps */}
          {intermediateStops.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <label>Zwischenstopps:</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {intermediateStops.map((stop, index) => (
                  <span key={index} style={{ 
                    background: '#f3f4f6',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.875rem'
                  }}>
                    {stop}
                    <button 
                      onClick={() => setIntermediateStops(intermediateStops.filter((_, i) => i !== index))}
                      style={{ marginLeft: '0.5rem', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button 
              onClick={() => handleAddStop()}
              style={{ 
                padding: '0.5rem 1rem',
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              + Zwischenstopp
            </button>
            
            <button 
              onClick={() => setIntermediateStops([])}
              style={{ 
                padding: '0.5rem 1rem',
                background: '#fee2e2',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Stopps löschen
            </button>
          </div>
        </div>

        {/* Schnelle Test-Routen */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3>Schnelle Test-Routen:</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {testRoutes.map((route, index) => (
              <button
                key={index}
                onClick={() => handleTestRoute(route.start, route.destination)}
                style={{ 
                  padding: '0.5rem 1rem',
                  background: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {route.label}
              </button>
            ))}
          </div>
        </div>

        {/* Karte */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3>Karte:</h3>
          {startCity && destinationCity ? (
            <div>
              <div style={{ 
                background: '#f8fafc',
                padding: '0.75rem 1rem',
                borderRadius: '8px 8px 0 0',
                border: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>
                  <Navigation size={16} /> {startCity} → {destinationCity}
                </span>
                {intermediateStops.length > 0 && (
                  <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    {intermediateStops.length} Stopp(s)
                  </span>
                )}
              </div>
              
              {/* Hier wird die Karte gerendert */}
              <RideMap 
                startCity={startCity} 
                destinationCity={destinationCity}
                intermediateStops={intermediateStops}
                showETA={true}
              />
            </div>
          ) : (
            <div style={{ 
              background: '#f8fafc',
              padding: '2rem',
              borderRadius: '8px',
              border: '2px dashed #cbd5e1',
              textAlign: 'center'
            }}>
              <MapPin size={32} style={{ marginBottom: '1rem', color: '#94a3b8' }} />
              <p>Bitte Start- und Zielstadt eingeben</p>
            </div>
          )}
        </div>

        {/* ETA Preview */}
        <div>
          <h3>ETA Preview (für Ride Cards):</h3>
          <div style={{ 
            background: 'white',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <RideMapPreview startCity={startCity} destinationCity={destinationCity} />
          </div>
        </div>

        {/* Debug Info */}
        <div style={{ 
          marginTop: '2rem',
          padding: '1rem',
          background: '#fefce8',
          border: '1px solid #fde047',
          borderRadius: '8px',
          fontSize: '0.875rem'
        }}>
          <h4 style={{ marginTop: 0 }}>Debug Info:</h4>
          <ul>
            <li>Start: {startCity}</li>
            <li>Ziel: {destinationCity}</li>
            <li>Zwischenstopps: {intermediateStops.join(', ') || 'Keine'}</li>
            <li>Backend: {backendUrl}</li>
            <li>Leaflet Version: 1.9.4</li>
            <li>React-Leaflet Version: 5.0.0</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TestMapPage;